import { Component, OnInit, OnDestroy, Input, ViewChild, Output, EventEmitter, DOCUMENT, inject } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { TabSessionService } from 'src/app/services/tab-session.service';

import { UpdatePinComponent } from '../update-pin/update-pin.component';
import { WitnessAuthenticationComponent } from '../witness-authentication/witness-authentication.component';
import { NotificationManagementService } from 'src/app/services/notification-management.service';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { EMPTY, finalize, Subject, timer } from 'rxjs';
import { ThemePreferenceService } from 'src/app/services/theme-preference.service';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

const NOTIFICATION_POLL_INTERVAL_MS = 60_000;

interface HeaderNotification {
  message: string;
  createdAt: string;
  seen: boolean;
  deleted: boolean;
  [key: string]: unknown;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  authService = inject(AuthService);
  private tabSessionService = inject(TabSessionService);
  private notificationManagementService = inject(NotificationManagementService);
  private messageService = inject(MessageService);
  private themePreferenceService = inject(ThemePreferenceService);
  private document = inject<Document>(DOCUMENT);
  private readonly destroying$ = new Subject<void>();

  @Input() showWitness = false;
  @Input() deId?: boolean;
  @Input() dissableDeIdSwitch = false;
  @Output() deIdChange = new EventEmitter<boolean>();

  @ViewChild('witnessAuthDialog') witnessAuthDialog!: WitnessAuthenticationComponent;
  @ViewChild('updatePinDialog') updatePinDialog! : UpdatePinComponent;

  skinPath = environment.assets;
  customerName = environment.customerName;
  versionType = environment.userAuthVersion
  wasteActive = environment.wasteActive
  logoPath = "/assets/images/all_logos/" + environment.logoFile;
  displayPosition = false;
  visible = false;
  position = '';

  userNotifactions: any = [];
  notification: any =[];
  showRecentlyDeleted = false;
  recentlyDeleted: any =[];
  notificationCount: any = 0;

  utilitiesMenuItems: any[] = [];
  themePreferenceSaving = false;

  get themeActionLabel(): string {
    return this.themePreferenceService.currentPreference === 'dark-mode'
      ? 'Light mode'
      : 'Dark mode';
  }

  get themeActionAriaLabel(): string {
    return `Switch to ${this.themeActionLabel.toLowerCase()}`;
  }

  get isDarkMode(): boolean {
    return this.themePreferenceService.currentPreference === 'dark-mode';
  }

  get effectiveDeId(): boolean {
    return this.deId ?? this.authService.getDeIdentifiedMode();
  }

  selectedOptions: any;
  treeOptions: any = [
    {
      key: '0',
      label: 'DD Watch',
      data: 'DDWatch',
      icon: 'pi pi-fw pi-eye',
          children: [
            { key: '0-0', label: 'DD Watch 2022', icon: 'pi pi-fw pi-file-pdf', path: 'DDWatch2022' },
            { key: '0-1', label: 'DD Watch 2023', icon: 'pi pi-fw pi-file-pdf', path: 'DDWatch2023' },
            { key: '0-1', label: 'DD Watch 2024', icon: 'pi pi-fw pi-file-pdf', path: 'DDWatch2024' },
          ]
    },
    {
      key: '1',
      label: 'User Guide',
      data: 'UserGuide',
      icon: 'pi pi-fw pi-users',
          children: [
            { key: '0-0', label: 'User Guide 2024', icon: 'pi pi-fw pi-file-pdf', path: 'UserGuide2024' },
          ]
    },
    {
      key: '2',
      label: 'Release Brief',
      data: 'ReleaseBrief',
      icon: 'pi pi-fw pi-briefcase',
          children: [
            { key: '0-0', label: 'release Brief 2022', icon: 'pi pi-fw pi-file-pdf', path: 'UserGuide2024' },
            { key: '0-0', label: 'release Brief 2023', icon: 'pi pi-fw pi-file-pdf', path: 'UserGuide2024' },
            { key: '0-0', label: 'release Brief 2024', icon: 'pi pi-fw pi-file-pdf', path: 'UserGuide2024' },
          ]
    },
  ];


  ngOnInit(): void {
    this.startNotificationPolling();
    this.setupUtilitiesMenu();
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  setupUtilitiesMenu() {
    this.utilitiesMenuItems = [];
    if (['DDPD', 'DDPC', 'DDPS'].includes(this.getUserRole())) {
      this.utilitiesMenuItems.push(
        {
          label: 'Helpful documents',
          icon: 'pi pi-file',
          command: () => {
            this.router.navigate(['/documents']);
          }
        }
      );
    }

    this.utilitiesMenuItems.push({
      label: 'Sign out',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    });
  }

  openUserMenu(event: Event, menu: Menu): void {
    this.setupUtilitiesMenu();
    menu.toggle(event);
  }


  logout(): void {
    void this.tabSessionService.logout();
  }

  switchTheme(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.themePreferenceSaving) {
      return;
    }

    const savedPreference = this.themePreferenceService.currentPreference;
    const nextPreference = this.themePreferenceService.alternatePreference;
    this.themePreferenceService.applyPreference(nextPreference);
    this.themePreferenceSaving = true;
    this.themePreferenceService.updatePreference(nextPreference)
      .pipe(finalize(() => this.themePreferenceSaving = false))
      .subscribe({
        next: (persistedPreference) => {
          if (this.authService.user) {
            this.authService.user.themePreference = persistedPreference;
          }
          if (persistedPreference !== this.themePreferenceService.currentPreference) {
            this.themePreferenceService.applyPreference(persistedPreference);
          }
        },
        error: () => {
          this.themePreferenceService.applyPreference(savedPreference);
          this.messageService.add({
            severity: 'error',
            summary: 'Theme update failed',
            detail: 'Your theme preference could not be saved. Please try again.'
          });
        }
      });
  }

  getUserRole(){
      if (this.authService.user === undefined || this.authService.user === null){
        return ''
      }
      else {return this.authService.user.role;
      }
  }

  getP2pLink(){
    return "/peer-to-peer/" + this.effectiveDeId.toString();
  }

  isP2PVisible(){
    if(environment.environment == "mayo-prod") {
      return false;
    }
    else{
      return true;
    }
  }

  authenticateWitness(){
    // this.router.navigate(['/waste-module']);
    this.witnessAuthDialog.showDialog = true;
  }

  showPincodeUpdateModal(){
    this.witnessAuthDialog.showDialog = false
    this.updatePinDialog.showDialog = true
  }
  showDialog(position: string) {
    this.position = position;
    this.getNotifications();
    this.visible = true;
  }

  private startNotificationPolling(): void {
    timer(0, NOTIFICATION_POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroying$),
        switchMap(() => this.notificationManagementService.getNotifications().pipe(
          catchError(() => EMPTY)
        ))
      )
      .subscribe((response: any) => {
        if (Array.isArray(response) && response[0] && Array.isArray(response[0].notifications)) {
          this.setNotifications(response[0].notifications);
        }
      });
  }

  getNotifications(): void {
    this.notificationManagementService.getNotifications().subscribe((response: any) => {
      if (Array.isArray(response) && response[0] && Array.isArray(response[0].notifications)) {
        this.setNotifications(response[0].notifications);
      }
    })
  }

  hideNotification(notification: any){
    notification.deleted = true;
    this.notificationManagementService.updateNotification(this.userNotifactions).subscribe(() => {      
      this.getNotifications();
    })
  }
  
  hideAllNotifications(){
    this.notificationManagementService.deleteAllNotification().subscribe(() => {
      this.getNotifications();
    })
  }

  private setNotifications(notifications: HeaderNotification[]): void {
    this.userNotifactions = notifications;
    this.notification = notifications.filter((item) => item.deleted === false);
    this.recentlyDeleted = notifications.filter((item) => item.deleted === true);
    this.notificationCount = this.notification.filter((item) => item.seen !== true).length;
  }

  setSeen($event: any, notification: any) {
    const checked = ($event.target as HTMLInputElement).checked;
    notification.seen = checked;
    this.notificationCount = this.notification.filter((item: HeaderNotification) => item.seen !== true).length;

    this.notificationManagementService.updateNotification(this.userNotifactions).subscribe({
      next: () => this.getNotifications(),
      error: () => {
        notification.seen = !checked;
        this.notificationCount = this.notification.filter((item: HeaderNotification) => item.seen !== true).length;
      }
    });
  }

  onShowModel() {
    this.document.body.classList.add("ovrhidden");
  }

  deIdUpdate(){
    this.deIdChange.emit(this.effectiveDeId)
  }
  
}
